import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';
import axios from 'axios';

const DepartmentAndRolesCreation = () => {
  const [name, setName] = useState('');
  const [roles, setRoles] = useState([]);
  const [customRole, setCustomRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [departmentId, setDepartmentId] = useState(null);
  const [editingRoleKey, setEditingRoleKey] = useState(null);
  const [editingRoleValue, setEditingRoleValue] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const locationDepartmentId = searchParams.get("departmentId") || null;
  const baselineRef = useRef(null);
  const editingInputRef = useRef(null);

  const buildSubmitPayload = ({
    id = departmentId,
    n = name,
    r = roles,
  } = {}) => ({
    id: id || null,
    name: n || '',
    role: (Array.isArray(r) ? r : []).map((rl) => ({
      id: rl.id || null,
      role: rl.role || '',
    })),
  });

  useEffect(() => {
    const run = async () => {
      if (locationDepartmentId) {
        setDepartmentId(locationDepartmentId);
        setLoading(true);
        try {
          const resp = await axios.get(
            `${process.env.REACT_APP_API_DEPARTMENT_LIST}/${locationDepartmentId}`,
            {
              headers: {
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
                'Content-Type': 'application/json',
              },
            }
          );

          const data = resp.data;
          console.log("Department", data);
          if (data?.success) {
            const dept = data?.data?.departmentWithRoles;
            setName(dept.name || '');
            setRoles((dept.role || []).map(r => ({ ...r, _key: makeKey(r.id) })));

            baselineRef.current = buildSubmitPayload({
              id: dept.id || locationDepartmentId,
              n: dept.name || '',
              r: dept.role || [],
            });
          } else {
            throw new Error('Failed to fetch department details');
          }
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: err?.response?.data?.message || err.message || 'Failed to fetch department details',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        } finally {
          setLoading(false);
        }
      } else {
        setRoles([]);
        baselineRef.current = buildSubmitPayload({ id: null, n: '', r: [] });
      }
    };
    run();
  }, [locationDepartmentId]);

  useEffect(() => {
    if (editingRoleKey && editingInputRef.current) {
      editingInputRef.current.focus();
    }
  }, [editingRoleKey]);


  const makeKey = (id) =>
    id != null ? String(id) : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;


  const handleAddRole = () => {
    if (editingRoleKey !== null) {
      if (editingInputRef.current) editingInputRef.current.blur();
      setEditingRoleKey(null);
      setEditingRoleValue('');
    }

    const next = (customRole || '').trim();
    if (!next) return;

    const exists = roles.some(r => (r.role || '').trim().toLowerCase() === next.toLowerCase());
    if (exists) return;

    setRoles([...roles, { role: next, _key: makeKey(null) }]);
    setCustomRole('');
  };


  const handleEdit = (e, roleKey, currentRole) => {
    e.preventDefault();
    setEditingRoleKey(roleKey);
    setEditingRoleValue(currentRole);
  };

  const handleSaveRole = (roleKey) => {
    setRoles(prev => prev.map(r => (r._key === roleKey ? { ...r, role: editingRoleValue } : r)));
    setEditingRoleKey(null);
    setEditingRoleValue('');
  };

  const handleRemoveRole = (roleKey) => {
    setRoles(prev => prev.filter(r => r._key !== roleKey));
    if (editingRoleKey === roleKey) {
      setEditingRoleKey(null);
      setEditingRoleValue('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (roles.length < 1) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'You must add at least 1 role before submitting.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const newPayload = buildSubmitPayload({});
    const oldPayload = baselineRef.current || {};
    console.log(oldPayload);
    console.log(newPayload);
    if (departmentId) {
      const { digest, html } = buildDiffPreview(oldPayload, newPayload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(
        process.env.REACT_APP_API_DEPARTMENT_CREATE,
        newPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        }
      );

      const data = resp.data;

      if (!(resp.status === 200 && data?.success)) {
        throw new Error(data?.success_message || 'Something went wrong!');
      }

      fireAudit({
        entity: 'department',
        affected_id: departmentId || null,
        action: departmentId ? 'update' : 'create',
        businessApi: process.env.REACT_APP_API_DEPARTMENT_CREATE,
        businessMethod: 'POST',
        oldPayload,
        newPayload,
        section: 'Organization',
        subSection: 'Department Creation',
        extra: { page: 'DepartmentCreation' },
      });

      Swal.fire({
        icon: 'success',
        title: departmentId ? 'Department Updated!' : 'Department Created!',
        text: data?.success_message || 'Success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      baselineRef.current = newPayload;

      setName('');
      setRoles([]);
      navigate(-1);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err?.response?.data?.message || err?.message || 'Failed to save department.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/departmentAndJob" className="breadcrumb-link">
          Department List
        </Link>{' '}
        &gt; {departmentId ? 'Update Department' : 'Department Creation'}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="floating-select-form">
          <input
            id="department-name"
            name="name"
            className={`walkin-form ${name ? 'has-value' : ''}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Department Name"
            required
          />
          {name && <label htmlFor="department-name">Department Name</label>}
        </div>

        <div style={{ overflow: 'scroll' }}>
          <fieldset className="enrollment-form-fieldset enrollment-form-mt-3" style={{ marginTop: 0 }}>
            <legend className="enrollment-form-legend">Roles</legend>
            <div className="academy-rule-input-group-container">
              <input
                type="text"
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
                placeholder="Enter Role"
                className="walkin-form"
                disabled={loading}
              />
              <button
                type="button"
                className="plusButton"
                onClick={handleAddRole}
                title="Add Role"
                disabled={loading || !customRole.trim()}
              >
                <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                  <g>
                    <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                  </g>
                </svg>
              </button>
            </div>
            <ul className="academy-rules-display-list">
              {roles.map((role) => (
                <li
                  key={role._key}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  {editingRoleKey === role._key ? (
                    <>
                      <input
                        ref={editingInputRef}
                        type="text"
                        value={editingRoleValue}
                        onChange={(e) => setEditingRoleValue(e.target.value)}
                        className="editing-role-input"
                      />
                      <button
                        type="button"
                        className="editing-role-save-btn"
                        onClick={() => handleSaveRole(role._key)}
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    <>
                      <span>{role.role}</span>
                      <div>
                        <button
                          className="edit-btn tooltip-btn"
                          onClick={(e) => handleEdit(e, role._key, role.role)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                          </svg>
                          <span className="tooltip-text">Edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveRole(role._key)}
                          style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '12px', background: 'white', border: 'none' }}
                          title="Remove role"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="red"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z"
                            />
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </fieldset>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? (departmentId ? 'Updating…' : 'Creating…') : (departmentId ? 'Update Department' : 'Create Department')}
        </button>
      </form>

      {/* Inline CSS in the same file */}
      <style>
        {`
    .editing-role-input {
      padding: 8px 12px;
      font-size: 14px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      outline: none;
      width: 220px;
      margin-right: 10px;
      transition: all 0.25s ease;
      background-color: #f9fafb;
    }

    .editing-role-input:focus {
      border-color: #2563eb;
      background-color: #ffffff;
      box-shadow: 0 0 6px rgba(37, 99, 235, 0.4);
    }

    .editing-role-save-btn {
      padding: 8px 14px;
      font-size: 14px;
      font-weight: 500;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    }

    .editing-role-save-btn:hover {
      background: linear-gradient(135deg, #2563eb, #1e40af);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    }

    .editing-role-save-btn:active {
      transform: scale(0.97);
      box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    }

    @media (max-width: 600px) {
      .editing-role-input {
        width: 100%;
        margin-bottom: 8px;
      }
      .editing-role-save-btn {
        width: 100%;
      }
    }
  `}
      </style>

    </div>
  );
};

export default DepartmentAndRolesCreation;